import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { FilePdf, ArrowRight, BookOpen } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ASSET_BASE = process.env.REACT_APP_BACKEND_URL || "";
const resolveUrl = (u) => !u ? "" : (u.startsWith("http") ? u : `${ASSET_BASE}/${u.replace(/^\/+/, "")}`);

export default function WhitepaperSection() {
  const [papers, setPapers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let m = true;
    axios.get(`${API}/research-papers`, { params: { type: "whitepaper" } })
      .then((r) => m && setPapers(r.data?.papers || []))
      .catch(() => {})
      .finally(() => m && setLoading(false));
    return () => { m = false; };
  }, []);

  // Always render the section (even before whitepapers are seeded) so admins
  // and visitors can see the "Whitepapers" placeholder on the home page.

  return (
    <section className="bg-gradient-to-b from-stone-950 to-stone-900/60 py-16 sm:py-20 border-y border-stone-900/80"
             data-testid="whitepaper-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex flex-col items-center text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-600/10 border border-emerald-700/30 mb-4">
            <BookOpen size={14} weight="duotone" className="text-emerald-400" />
            <span className="text-[10px] uppercase tracking-[0.18em] text-emerald-300 font-heading font-semibold">Whitepapers &amp; Research</span>
          </div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white">Deep dives into how the platform works</h2>
          <p className="text-sm text-stone-400 mt-3 max-w-xl font-body">
            Methodology descriptors, dataset architectures, and applied case studies. All open-access.
          </p>
        </div>

        {papers.length === 0 ? (
          <div className="max-w-2xl mx-auto text-center bg-stone-900/60 border border-stone-800 border-dashed rounded-2xl p-8"
               data-testid="whitepaper-empty">
            <BookOpen size={28} weight="duotone" className="text-emerald-400/70 mx-auto mb-3" />
            <p className="text-sm text-stone-300 font-heading">
              New whitepapers &amp; research papers publish here as they&apos;re released.
            </p>
            <Link to="/research-papers"
              className="inline-flex items-center gap-1 text-sm text-emerald-400 hover:text-emerald-300 mt-4">
              Visit the research library <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {papers.slice(0, 6).map((p) => (
              <a key={p.id} href={p.pdf_url ? resolveUrl(p.pdf_url) : (p.external_url || "#")}
                target="_blank" rel="noreferrer"
                data-testid={`whitepaper-${p.id}`}
                className="group bg-stone-900 rounded-2xl border border-stone-800 p-5 hover:border-emerald-700/50 transition-all duration-300 hover:-translate-y-0.5">
                <FilePdf size={26} weight="duotone" className="text-emerald-400 mb-3" />
                <p className="font-heading text-sm font-semibold text-white leading-snug">{p.title}</p>
                {p.authors && <p className="text-[11px] text-stone-500 mt-1">{p.authors}</p>}
                {p.abstract && <p className="text-xs text-stone-400 mt-2 line-clamp-3">{p.abstract}</p>}
                <div className="flex items-center gap-1 text-emerald-400 text-xs mt-4 group-hover:gap-2 transition-all">
                  Download <ArrowRight size={12} weight="bold" />
                </div>
              </a>
            ))}
          </div>
        )}

        {papers.length > 6 && (
          <div className="text-center mt-8">
            <Link to="/research-papers"
              className="inline-flex items-center gap-1 text-sm text-emerald-400 hover:text-emerald-300">
              View all research &amp; whitepapers <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
